<template>
  <div
    class="product-card product-card--ref-co"
    :class="`product-card--${productDocument.uid} product-card--${productDocument.type}`"
  >
    <nuxt-link
      :to="$contextSanityPath(productDocument)"
      class="d-block p-relative"
      @click.native="loading = true"
    >
      <div class="product-card-v2__image">
        <transition mode="in-out" name="product-card-image-switch">
          <lazy-image
            :images="images"
            :preload="isFirst"
            :force-show="isFirst"
            sizes="100vw"
          />
        </transition>
      </div>

      <div
        @mouseover="hovering = true"
        @mouseleave="
          hovering = false
          activeColor = -1
        "
      >
        <div
          v-waypoint="{
            active: !contentAnimated && timeline.initiated,
            callback: onContentWaypoint,
            options: intersectionOptions,
          }"
          class="product-card__content text-color-tertiary"
        >
          <p v-if="showNextBatch" ref="tag" class="tag tag--sun-and-white">
            <icon name="estimated-time" />
            <span>{{ $t('product.tag.last_chance') }}</span>
          </p>

          <p v-else-if="thisWeekOnly" class="tag tag--sun-and-white">
            <icon name="estimated-time" />
            <span> {{ $t('preco_of_the_week') }}</span>
          </p>

          <h2
            ref="productName"
            class="product-name text-color-tertiary mb-0-4 mb-md-0-8 pl-1 pr-1"
          >
            {{ productDocument.title }}
          </h2>

          <div
            v-if="
              productDocument.punchline && productDocument.punchline.length > 0
            "
            ref="punchline"
          >
            <p
              class="product-card__punchline text-uppercase w-600 pl-1 pr-1 mb-0-4 mb-md-0-8 text-color-tertiary text-center"
            >
              {{ productDocument.punchline }}
            </p>
          </div>

          <div ref="colors" class="d-flex justify-center">
            <transition
              v-if="distinctColors.length > 1"
              mode="out-in"
              @enter="colorEnter"
              @leave="colorLeave"
            >
              <div
                v-if="hovering"
                class="product-card__colors d-flex align-center flex-wrap"
              >
                <button
                  v-for="(color, index) in distinctColors"
                  :key="color._key"
                  type="button"
                  class="colors__matter"
                  :class="{ active: activeColor === index }"
                  :title="color.label"
                  @mouseenter="colorMatterMouseEnter(index)"
                  @click.prevent="goToColor(color._key)"
                >
                  <span
                    class="bg"
                    :style="`background-image: url(${getColorThumbnailBg(
                      color
                    )})`"
                  ></span>
                  <span class="visually-hidden">{{ color.label }}</span>
                </button>
              </div>
              <p
                v-else
                class="product-card__colors--number w-300 text-color-tertiary mb-0"
              >
                <span style="line-height: 34px">
                  {{
                    $t('card.product.number-of-colors', {
                      number: distinctColors.length,
                    })
                  }}
                </span>
              </p>
            </transition>
          </div>

          <big-count-down
            v-if="showOverSoon"
            ref="countdown"
            :date="productDocument.dates.end"
            home
            class="pt-0-6 pt-md-1"
          />
        </div>
      </div>
    </nuxt-link>
    <div class="container">
      <div class="row-md justify-center">
        <div class="col-md-11">
          <div v-if="refCoList.length > 1" class="product-card__refs">
            <product-ref-co-child
              v-for="(color, index) of refCoList"
              :key="getKey(color, index)"
              :color="color"
              :style="[
                gridItemsPosition[index],
                gridItemsPositionMobile[index],
                {
                  '--display-desktop': gridItemsPosition[index]
                    ? 'block'
                    : 'none',

                  '--display-mobile': gridItemsPositionMobile[index]
                    ? 'block'
                    : 'none',
                },
              ]"
              :type="
                gridItemsPosition[index]
                  ? gridItemsPosition[index].type
                  : undefined
              "
              :type-mobile="
                gridItemsPositionMobile[index]
                  ? gridItemsPositionMobile[index].type
                  : undefined
              "
              :to="prepareForQuery(color._key)"
              :price="getColorPrice(color)"
              :compare-at-price="getColorPrice(color, true)"
              :hide-tags="hideTags"
            />
          </div>
        </div>
        <div v-html="gridStyle"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { differenceInDays, isFuture, isSameWeek } from 'date-fns'
import NuxtSSRScreenSize from 'nuxt-ssr-screen-size'

import LazyImage from '@/components/Image/LazyImage'
import Icon from '@/components/icons/Icon'
import ProductRefCoChild from '@/components/Product/RefCoChild'
// import VideoBunny from '@/components/video/bunny'

import { BUNNY_STREAM_FOLDERS } from '@/const'

import { NUMBER_BY_LINE_ITEMS } from '@/const/gallery'
import { getSanityBunnyImageLink } from '@/utils/image'
import { PRODUCT_IMAGE_FORMAT } from '@/const/image'
import { checkVisible } from '@/utils/tools'

export default {
  name: 'ProductCardRefCo',
  components: {
    LazyImage,
    Icon,
    ProductRefCoChild,
    // VideoBunny,
    BigCountDown: () =>
      import(
        '@/components/countdown/BigCountDown' /* webpackChunkName: 'big_countdown' */
      ),
  },
  mixins: [NuxtSSRScreenSize.NuxtSSRScreenSizeMixin],
  props: {
    productDocument: {
      type: Object,
      required: true,
    },
    shopifyProduct: {
      type: Object,
      default: null,
    },
    colorsOrder: {
      type: Array,
      default: () => [],
    },
    gallery: {
      type: Array,
      default: () => [],
    },
    galleryMobile: {
      type: Array,
      default: () => [],
    },
    isFirst: {
      type: Boolean,
      default: false,
    },
    hideTags: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    contentAnimated: false,
    intersectionOptions: {
      root: null,
      rootMargin: '150px 0px 0px 0px',
      threshold: [0, 1],
    },
    refsInScreen: [],
    windowHeight: 0,
    timeline: {
      initiated: false,
      content: null,
    },
    linkedShopifyProducts: {},
    activeColor: -1,
    hovering: false,
    BUNNY_STREAM_FOLDERS,
  }),
  head() {
    if (this.isFirst) {
      if (this.isOnHome) {
        if (this.productDocument.image.top_of_image_is_light) {
          return {
            htmlAttrs: {
              class: ['header--dark'],
            },
          }
        }
      } else if (this.productDocument.image_eshop?.image) {
        if (this.productDocument.image_eshop.top_of_image_is_light) {
          return {
            htmlAttrs: {
              class: ['header--dark'],
            },
          }
        }
      } else if (this.productDocument.image.top_of_image_is_light) {
        return {
          htmlAttrs: {
            class: ['header--dark'],
          },
        }
      }
    }
    return {}
  },
  computed: {
    isOnHome() {
      return this.$route.name.startsWith('context__')
    },
    images() {
      const imgs = []
      let image = this.productDocument.image.image

      if (this.activeColor > -1) {
        const color = this.distinctColors[this.activeColor]
        if (color) {
          image = color.image
        }
      } else if (this.productDocument.image_eshop?.image && !this.isOnHome) {
        image = this.productDocument.image_eshop.image
      }

      // MOBILE
      imgs.push({
        image: {
          url: getSanityBunnyImageLink(image, PRODUCT_IMAGE_FORMAT.LIST),
          dimensions: PRODUCT_IMAGE_FORMAT.LIST,
        },
        widths: [380, 570, 760],
        media: {
          type: 'max',
          value: 769,
        },
      })

      // DESKTOP
      imgs.push({
        image: {
          url: getSanityBunnyImageLink(image, PRODUCT_IMAGE_FORMAT.FULL),
          dimensions: PRODUCT_IMAGE_FORMAT.FULL,
        },
        media: {
          type: 'min',
          value: 770,
        },
        widths: [1440, 2160, 2880],
        sizes: '100vw',
      })

      return imgs
    },
    hasPunchLine() {
      return this.productDocument.punchline
    },
    refCoList() {
      let colors = [...this.productDocument.colors].map((c) => {
        let colorKey = c._key
        let thirdLabel = null

        if (this.productDocument.has_third_option) {
          const [thirdKey, colorId] = c._key.split('__')
          colorKey = colorId

          const thirdValues = this.productDocument.shopify_product.options.find(
            (o) => o.position === 3
          )?.values

          thirdLabel = thirdValues?.find((v) => v._key === thirdKey)?.name || ''
        }

        const colorName =
          this.productDocument.shopify_product.colorOptions.find(
            (co) => co._key === colorKey
          )?.name || ''

        if (thirdLabel) {
          c.label = `${colorName} / ${thirdLabel}`
        } else {
          c.label = `${colorName}`
        }

        return c
      })

      if (
        this.productDocument.has_linked_product &&
        this.productDocument.linked_products
      ) {
        const linkedProducts = this.productDocument.linked_products.filter(
          (p) => p.product._id !== this.productDocument._id
        )

        colors = [
          ...colors,
          ...linkedProducts
            .map((p) =>
              p.product.colors.map((c) => {
                let colorKey = c._key
                let thirdLabel = null

                if (this.productDocument.has_third_option) {
                  const [thirdKey, colorId] = c._key.split('__')
                  colorKey = colorId

                  const thirdValues =
                    this.productDocument.shopify_product.options.find(
                      (o) => o.position === 3
                    )?.values

                  thirdLabel =
                    thirdValues?.find((v) => v._key === thirdKey)?.name || ''
                }

                const colorName =
                  this.productDocument.shopify_product.colorOptions.find(
                    (co) => co._key === colorKey
                  )?.name || ''

                if (thirdLabel) {
                  c.label = `${colorName} / ${thirdLabel}`
                } else {
                  c.label = `${colorName}`
                }

                return {
                  ...c,
                  _key: `${p.product._id}___${c._key}`,
                }
              })
            )
            .flat(),
        ]
      }

      const colorsOrderMap = new Map(this.colorsOrder.map((c, i) => [c, i]))

      return colors.sort(
        (a, b) => colorsOrderMap.get(a._key) - colorsOrderMap.get(b._key)
      )
    },
    gridStyle() {
      return `<style>
        .product-card--${this.productDocument.uid} .product-card__refs {
          --number-of-lines: ${this.numberOfLinesMobile};
          ${this.gridTemplateRowsMobile}

          @media screen and (min-width: 769px) {
            --number-of-lines: ${this.numberOfLines};
            ${this.gridTemplateRows}
          }
        }
      </style>`
    },
    showNextBatch() {
      if (
        this.productDocument.current_batch_date_end &&
        this.productDocument.next_delivery_date
      ) {
        return (
          isFuture(this.productDocument.current_batch_date_end) &&
          differenceInDays(
            this.productDocument.current_batch_date_end,
            new Date()
          ) <= 14
        )
      }
      return false
    },
    showOverSoon() {
      if (this.productDocument.dates.end) {
        return (
          isFuture(this.productDocument.dates.end) &&
          differenceInDays(this.productDocument.dates.end, new Date()) <= 10
        )
      }

      return false
    },
    thisWeekOnly() {
      if (this.productDocument.dates.start && this.showOverSoon) {
        return isSameWeek(
          this.productDocument.dates.start,
          this.productDocument.dates.end,
          {
            weekStartsOn: 1,
          }
        )
      }

      return false
    },
    distinctColors() {
      if (
        this.productDocument.shopify_product.options.length <= 0 ||
        !this.productDocument.third_options ||
        this.productDocument.third_options.layout === 'as_label'
      ) {
        return this.productDocument.colors
      }

      const cc = []

      for (const color of this.productDocument.shopify_product.colorOptions) {
        if (!cc.some((c) => c._key.endsWith(color._key))) {
          const productDocumentColor = this.productDocument.colors.find((c) =>
            c._key.endsWith(color._key)
          )
          if (productDocumentColor) {
            cc.push(productDocumentColor)
          }
        }
      }

      cc.map((c) => {
        const colorId = c._key.split('__').pop()

        c.label =
          this.productDocument.shopify_product.colorOptions.find(
            (co) => co._key === colorId
          )?.name || ''

        return c
      })

      return cc
    },

    numberOfLines() {
      return this.gallery.length
    },
    numberOfLinesMobile() {
      return this.galleryMobile.length
    },

    gridTemplateRows() {
      return `grid-template-rows: ${this.gallery
        .map((line) => this.getHeightOfLine(line))
        .join(' ')};`
    },
    gridTemplateRowsMobile() {
      return `grid-template-rows: ${this.galleryMobile
        .map((line) => this.getHeightOfLine(line))
        .join(' ')};`
    },

    gridItemsPosition() {
      return this.gallery
        .map((line, index) => {
          return this.getGridLineItemsPositions(line, index)
        })
        .flat()
    },
    gridItemsPositionMobile() {
      return this.galleryMobile
        .map((line, index) => {
          return this.getGridLineItemsPositions(line, index, true)
        })
        .flat()
    },

    numberOfPlaces() {
      return this.gallery
        .map((line) => {
          return this.getNumberOfPlaceByLine(line)
        })
        .reduce((acc, val) => acc + val, 0)
    },

    numberOfPlacesMobile() {
      return this.galleryMobile
        .map((line) => {
          return this.getNumberOfPlaceByLine(line)
        })
        .reduce((acc, val) => acc + val, 0)
    },
  },
  mounted() {
    this.initAnim()
    this.windowHeight = window.innerHeight
  },
  methods: {
    async initAnim() {
      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)

      this.timeline.content = gsap.timeline({
        onComplete: () => {
          this.contentAnimated = true
        },
      })

      if (checkVisible(this.$el)) {
        this.contentAnimated = true
        return
      }

      let start = 0

      if (this.$refs.tag) {
        gsap.set(this.$refs.tag, {
          opacity: 0,
          y: -20,
        })

        this.timeline.content.to(
          this.$refs.tag,
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: 'power2.out',
          },
          start
        )
        start += 0.2
      }

      if (this.$refs.productName) {
        gsap.set(this.$refs.productName, {
          opacity: 0,
          y: -20,
        })
        this.timeline.content.to(
          this.$refs.productName,
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: 'power2.out',

            onComplete: () => {
              this.$refs.productName.style = ''
            },
          },
          start
        )
        start += 0.1
      }

      if (this.$refs.punchline) {
        gsap.set(this.$refs.punchline, {
          opacity: 0,
          y: -20,
        })
        this.timeline.content.to(
          this.$refs.punchline,
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: 'power2.out',

            onComplete: () => {
              this.$refs.colors.style = ''
            },
          },
          start
        )
        start += 0.1
      }

      if (this.$refs.colors) {
        gsap.set(this.$refs.colors, {
          opacity: 0,
          y: -20,
        })
        this.timeline.content.to(
          this.$refs.colors,
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: 'power2.out',

            onComplete: () => {
              this.$refs.colors.style = ''
            },
          },
          start
        )
        start += 0.1
      }

      if (this.$refs.countdown && this.$refs.countdown.$el) {
        gsap.set(this.$refs.countdown.$el, {
          opacity: 0,
          y: -20,
        })
        this.timeline.content.to(
          this.$refs.countdown.$el,
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: 'power2.out',
          },
          start
        )
      }

      this.timeline.content.pause()
      this.timeline.initiated = true
    },
    getHeightOfLine(numberOfProductsPerLine) {
      switch (numberOfProductsPerLine) {
        case NUMBER_BY_LINE_ITEMS.ONE:
          return 'var(--grid-line-height-1-p)'
        case NUMBER_BY_LINE_ITEMS.TWO:
          return 'var(--grid-line-height-2-p)'
        case NUMBER_BY_LINE_ITEMS.THREE:
          return 'var(--grid-line-height-3-p)'
        case NUMBER_BY_LINE_ITEMS.FOUR:
          return 'var(--grid-line-height-4-p)'
        default:
          return 'var(--grid-line-height-3-p)'
      }
    },
    getNumberOfPlaceByLine(numberOfProductsPerLine) {
      switch (numberOfProductsPerLine) {
        case NUMBER_BY_LINE_ITEMS.ONE:
          return 1
        case NUMBER_BY_LINE_ITEMS.TWO:
          return 2
        case NUMBER_BY_LINE_ITEMS.THREE:
          return 3
        case NUMBER_BY_LINE_ITEMS.FOUR:
          return 4
        default:
          return 3
      }
    },
    getGridLineItemsPositions(
      numberOfProductsPerLine,
      currentLine,
      mobile = false
    ) {
      switch (numberOfProductsPerLine) {
        case NUMBER_BY_LINE_ITEMS.ONE:
          return [
            {
              [mobile ? '--grid-column-mobile' : '--grid-column']: '1 / 13',
              [mobile
                ? '--grid-row-mobile'
                : '--grid-row']: `${currentLine} / ${currentLine + 1}`,
              type: 'full',
            },
          ]
        case NUMBER_BY_LINE_ITEMS.TWO:
          return [
            {
              [mobile ? '--grid-column-mobile' : '--grid-column']: '1 / 7',
              [mobile
                ? '--grid-row-mobile'
                : '--grid-row']: `${currentLine} / ${currentLine + 1}`,
              type: 'half',
            },
            {
              [mobile ? '--grid-column-mobile' : '--grid-column']: '7 / 13',
              [mobile
                ? '--grid-row-mobile'
                : '--grid-row']: `${currentLine} / ${currentLine + 1}`,
              type: 'half',
            },
          ]
        case NUMBER_BY_LINE_ITEMS.THREE:
          return [
            {
              [mobile ? '--grid-column-mobile' : '--grid-column']: '1 / 5',
              [mobile
                ? '--grid-row-mobile'
                : '--grid-row']: `${currentLine} / ${currentLine + 1}`,
              type: 'list',
            },
            {
              [mobile ? '--grid-column-mobile' : '--grid-column']: '5 / 9',
              [mobile
                ? '--grid-row-mobile'
                : '--grid-row']: `${currentLine} / ${currentLine + 1}`,
              type: 'list',
            },
            {
              [mobile ? '--grid-column-mobile' : '--grid-column']: '9 / 13',
              [mobile
                ? '--grid-row-mobile'
                : '--grid-row']: `${currentLine} / ${currentLine + 1}`,
              type: 'list',
            },
          ]
        case NUMBER_BY_LINE_ITEMS.FOUR:
          return [
            {
              [mobile ? '--grid-column-mobile' : '--grid-column']: '1 / 4',
              [mobile
                ? '--grid-row-mobile'
                : '--grid-row']: `${currentLine} / ${currentLine + 1}`,
              type: 'list',
            },
            {
              [mobile ? '--grid-column-mobile' : '--grid-column']: '4 / 7',
              [mobile
                ? '--grid-row-mobile'
                : '--grid-row']: `${currentLine} / ${currentLine + 1}`,
              type: 'list',
            },
            {
              [mobile ? '--grid-column-mobile' : '--grid-column']: '7 / 10',
              [mobile
                ? '--grid-row-mobile'
                : '--grid-row']: `${currentLine} / ${currentLine + 1}`,
              type: 'list',
            },
            {
              [mobile ? '--grid-column-mobile' : '--grid-column']: '10 / 13',
              [mobile
                ? '--grid-row-mobile'
                : '--grid-row']: `${currentLine} / ${currentLine + 1}`,
              type: 'list',
            },
          ]
        default:
          return []
      }
    },
    getKey(color, index) {
      if (color.type === 'edito') {
        return `edito-${index}`
      }

      if (color.type === 'video') {
        return `video-${index}`
      }

      // TODO : LINKED PRODUCT
      // if (color.linkedVariantName) {
      //   return `${color._key}${color.linkedVariantName}-${index}`
      // }

      return `${color._key}-${index}`
    },
    getColorPrice(color, compare = false) {
      let colorKey = color._key
      let productDocument = this.productDocument

      if (
        this.productDocument.has_linked_product &&
        this.productDocument.linked_products
      ) {
        const [productId, colorAndMaybeThird] = color._key.split('___')

        const linkedProduct = this.productDocument.linked_products.find(
          (p) => p.product._id === productId
        )?.product

        if (linkedProduct) {
          productDocument = linkedProduct
          colorKey = colorAndMaybeThird
        }
      }

      if (colorKey.includes('__')) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const [third, colorValue] = colorKey.split('__')

        colorKey = colorValue
      }

      if (compare) {
        return (
          productDocument.shopify_product.colorOptions?.find(
            (c) => c._key === colorKey
          )?.compareAtPriceRange?.minVariantPrice || null
        )
      }

      return (
        productDocument.shopify_product.colorOptions?.find(
          (c) => c._key === colorKey
        )?.priceRange?.minVariantPrice || null
      )
    },
    getColorThumbnailBg(color) {
      return getSanityBunnyImageLink(color.thumbnail, {
        width: 100,
        height: 100,
      })
    },
    colorMatterMouseEnter(index) {
      this.activeColor = index
    },
    goToColor(color) {
      if (this.soonInSell) {
        return
      }

      this.loading = true
      if (color.includes('__')) {
        const [third, colorValue] = color.split('__')

        const query = {
          ...this.params,
          color: colorValue,
        }

        const thirdVariantKey =
          this.productDocument.shopify_product.options.find(
            (o) => o.position === 3
          )?._key || null

        if (thirdVariantKey) {
          query[thirdVariantKey] = third
        }

        this.$router.push({
          path: this.$contextSanityPath(this.productDocument),
          query,
        })
      }

      this.$router.push({
        path: this.$contextSanityPath(this.productDocument),
        query: { ...this.params, color },
      })
    },
    prepareForQuery(color) {
      const query = {}

      let colorKey = color
      let productTarget = this.productDocument

      if (color.includes('___')) {
        const [productId, colorValue] = color.split('___')
        colorKey = colorValue

        productTarget =
          this.productDocument.linked_products.find(
            (p) => p.product._id === productId
          )?.product ?? this.productDocument
      }

      if (color.includes('__')) {
        const [third, colorValue] = color.split('__')
        colorKey = colorValue

        const thirdVariantKey =
          this.productDocument.shopify_product.options.find(
            (o) => o.position === 3
          )?._key || null

        if (thirdVariantKey) {
          query[thirdVariantKey] = third
        }
      }

      query.color = colorKey

      return {
        path: this.$contextSanityPath(productTarget),
        query,
      }
    },
    onContentWaypoint(waypoint) {
      if (waypoint.going === this.$waypointMap.GOING_IN) {
        if (this.timeline.content && !this.timeline.content.isActive()) {
          this.timeline.content.restart()
        }
      }
    },
    async colorEnter(el, done) {
      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)

      if (el.classList.contains('product-card__colors')) {
        const buttons = el.getElementsByClassName('colors__matter')

        gsap.from(buttons, {
          opacity: 0,
          x: 20,
          stagger: {
            ease: 'power1.out',
            amount: 0.2,
          },
          duration: 0.4,
          onComplete() {
            return done()
          },
        })
      } else {
        return gsap.from(el, {
          opacity: 0,
          duration: 0.3,
          onComplete() {
            return done()
          },
        })
      }
    },
    async colorLeave(el, done) {
      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)

      return gsap.to(el, {
        opacity: 0,
        duration: 0.3,
        onComplete() {
          return done()
        },
      })
    },
  },
}
</script>

<style lang="scss">
.product-card--ref-co {
  .product-card__content {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    padding-bottom: 3svh;
  }

  .product-card-v2__image {
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(
        180deg,
        RGB(56 24 0 / 0) 60%,
        RGB(56 24 0 / 0.4) 100%
      );
    }
  }

  .product-name {
    text-align: center;
    font-size: rem(42px);

    @include mq($from: tablet) {
      font-size: rem(54px);
    }

    .determinant {
      display: block;
      font-size: rem(20px);
      line-height: 1.4;
    }
  }

  .product-card__refs {
    --number-of-lines: 1;

    display: grid;
    grid-template-columns: repeat(12, 1fr);
    grid-gap: calc(var(--spacing));
    grid-template-rows: repeat(var(--number-of-lines), auto);
    margin-top: calc(var(--spacing));

    @include mq($from: tablet) {
      grid-gap: calc(var(--spacing));
    }
  }

  .product-card__ref {
    --grid-column: 1 / 13;
    --grid-row: 1 / 2;
    --grid-column-mobile: 1 / 13;
    --grid-row-mobile: 1 / 2;

    position: relative;
    overflow: hidden;
    grid-column: var(--grid-column-mobile);
    grid-row: var(--grid-row-mobile);

    @include mq($from: tablet) {
      grid-column: var(--grid-column);
      grid-row: var(--grid-row);
      display: var(--display-desktop, block);
    }

    @include mq($until: tablet) {
      display: var(--display-mobile, block);
    }

    img {
      transition: all 0.3s var(--asphalte-animation-function);
    }

    &__image {
      @include mq($until: tablet) {
        position: relative;

        &::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(
            180deg,
            RGB(56 24 0 / 0) 60%,
            RGB(56 24 0 / 0.4) 100%
          );
        }
      }
    }

    &__content {
      position: relative;
      transition: all 0.3s var(--asphalte-animation-function);

      .inner {
        padding: calc(var(--spacing) * 0.6) 0;
        transition: all 0.5s var(--asphalte-animation-function);

        @include mq($until: tablet) {
          flex-wrap: wrap;
        }

        @include mq($from: tablet) {
          padding: var(--spacing) 0 calc(var(--spacing) * 0.8)
            calc(var(--spacing) * 0.4);
        }
      }

      .tag {
        position: absolute;
        left: calc(var(--spacing) * 0.4);
        bottom: 100%;

        @include mq($from: tablet) {
          transform: translateY(calc(var(--spacing) * 0.4));
        }

        @include mq($until: tablet) {
          span {
            font-size: var(--extra-small-font-size);
            line-height: 1;
          }
        }
      }
    }

    &__name {
      font-family: var(--secondary-font-family);
      font-size: var(--color-price-font-size);
      line-height: 1;
      transition: border 0.5s var(--asphalte-animation-function);
      white-space: nowrap;
      text-overflow: ellipsis;

      @include mq($from: tablet) {
        padding-right: var(--spacing);
        margin-right: var(--spacing);
      }

      @include mq($until: tablet) {
        flex-basis: 90vw;
        display: flex;
        align-items: center;
      }
    }

    &__icon {
      @include mq($from: tablet) {
        margin-left: auto;
      }
    }

    .price {
      transition: color 0.5s var(--asphalte-animation-function);

      .number {
        font-size: var(--color-price-font-size);
        line-height: 1;
      }
    }

    .compare {
      opacity: 0.8;

      @include mq($from: tablet) {
        opacity: 0.6;
      }

      .number {
        text-decoration: line-through;
      }
    }

    &--refco {
      @include on-hover-and-focus {
        img {
          transform: scale(1.05);
          transition: all 0.3s var(--asphalte-animation-function);
        }
      }
    }
  }

  &.product-card--normal {
    h2 {
      @include mq($until: tablet) {
        font-size: var(--h1-font-size);
      }
    }
  }

  + .product-card--ref-co {
    margin-top: calc(var(--spacing) * 4);
  }
}
</style>
